import { useAuth, type AuthStatus } from '@saay/auth-web';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';

import { assertPreferenceKey, type PreferenceKey, type PreferenceValueMap } from './keys.js';
import {
  buildPreferenceStorageKey,
  readPreferenceRaw,
  subscribePreferenceStore,
  type WebAppId,
  writePreferenceRaw,
} from './store.js';
import { ANONYMOUS_SUBJECT_HASH, hashSubject } from './subject-hash.js';

function resolveSubjectHash(status: AuthStatus, subject: string | undefined): string | null {
  if (status === 'initializing') {
    return null;
  }
  if (status === 'authenticated' && subject !== undefined) {
    return hashSubject(subject);
  }
  return ANONYMOUS_SUBJECT_HASH;
}

interface PreferenceScopeValue {
  readonly appId: WebAppId;
  readonly subjectHash: string | null;
}

const PreferenceScopeContext = createContext<PreferenceScopeValue | null>(null);

function parsePreferenceValue<K extends PreferenceKey>(
  key: K,
  raw: string | null,
  defaultValue: PreferenceValueMap[K],
): PreferenceValueMap[K] {
  if (raw === null) {
    return defaultValue;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (key === 'branchId' || key === 'cityId') {
      return (
        typeof parsed === 'string' || parsed === null ? parsed : defaultValue
      ) as PreferenceValueMap[K];
    }
    if (key === 'lastInstallmentIntervalDays') {
      return (
        typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : defaultValue
      ) as PreferenceValueMap[K];
    }
    if (key === 'tableColumns' || key === 'dismissedNotices') {
      return (
        Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')
          ? parsed
          : defaultValue
      ) as PreferenceValueMap[K];
    }
    if (key === 'listFilters') {
      return (
        parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
          ? (parsed as PreferenceValueMap[K])
          : defaultValue
      ) as PreferenceValueMap[K];
    }
    return defaultValue;
  } catch {
    return defaultValue;
  }
}

function serializePreferenceValue<K extends PreferenceKey>(value: PreferenceValueMap[K]): string {
  return JSON.stringify(value);
}

export function PreferenceProvider({
  appId,
  children,
}: {
  readonly appId: WebAppId;
  readonly children: ReactNode;
}) {
  const { principal, status } = useAuth();

  const value = useMemo(
    () => ({
      appId,
      subjectHash: resolveSubjectHash(status, principal?.subject),
    }),
    [appId, principal?.subject, status],
  );

  return (
    <PreferenceScopeContext.Provider value={value}>{children}</PreferenceScopeContext.Provider>
  );
}

function usePreferenceScope(): PreferenceScopeValue {
  const scope = useContext(PreferenceScopeContext);
  if (scope === null) {
    throw new Error('usePreference must be used inside <PreferenceProvider>.');
  }
  return scope;
}

export function usePreference<K extends PreferenceKey>(
  key: K,
  defaultValue: PreferenceValueMap[K],
): readonly [PreferenceValueMap[K], (next: PreferenceValueMap[K]) => void] {
  assertPreferenceKey(key);
  const { appId, subjectHash } = usePreferenceScope();
  const storageKey =
    subjectHash === null ? null : buildPreferenceStorageKey(appId, subjectHash, key);

  const snapshot = useSyncExternalStore(
    subscribePreferenceStore,
    () => {
      if (storageKey === null) {
        return serializePreferenceValue(defaultValue);
      }
      const raw = readPreferenceRaw(storageKey);
      return serializePreferenceValue(parsePreferenceValue(key, raw, defaultValue));
    },
    () => serializePreferenceValue(defaultValue),
  );

  const value = useMemo(
    () => parsePreferenceValue(key, snapshot, defaultValue),
    [defaultValue, key, snapshot],
  );

  const setValue = useCallback(
    (next: PreferenceValueMap[K]) => {
      if (storageKey === null) {
        return;
      }
      writePreferenceRaw(storageKey, serializePreferenceValue(next));
    },
    [storageKey],
  );

  return [value, setValue];
}
